/**
 * Job-signal domain types — productionised from the Storybook prototype (`prototypes/job-signal/mockSignal.ts`)
 * onto the live `/ai/skill-lint` verdict vocabulary. `Verdict` is defined here locally rather than pulled from
 * `apiSchema.d.ts`, because finlay-api emits `verdict` as a bare `string` (the deliberate "dumb pipe" — see the
 * plan §1c); `toVerdict` in `./verdict` narrows the wire string and degrades an unexpected value to `unknown`.
 */

import type { JobState } from '@/modules/jobs/jobStates';
import type { components } from '@/types/apiSchema';

export type Verdict =
  | 'searchable'
  | 'niche'
  | 'ubiquitous'
  | 'vague'
  | 'unknown';

/**
 * A job-title verdict from `/ai/title-lint` — a deliberately conservative binary: `specific` is the default and
 * `vague` means too broad or not a real title. Unrelated to the skill {@link Verdict} vocabulary above.
 */
export type TitleVerdict = 'specific' | 'vague';

export interface SkillVerdict {
  /** what the recruiter committed (trimmed) */
  input: string;
  /** canonical form from the vocabulary, or the input itself when unknown */
  canonical: string;
  verdict: Verdict;
  /** conventionally-cased form served by the vocabulary (`.net` → `.NET`), or null when it has none (plan 9.8) */
  display?: string | null;
}

/** A skill band as returned by `/ai/skill-search` — the 4-value subset of {@link Verdict} (no `vague`). */
export type Band = Exclude<Verdict, 'vague'>;

/** An autocomplete suggestion from `/ai/skill-search`. */
export interface SkillSuggestion {
  canonical: string;
  band: Band;
  /** conventionally-cased form served by the vocabulary (`.net` → `.NET`), or null when it has none (plan 9.8) */
  display?: string | null;
}

/* ── "What we need to know" — the inputs that sharpen who we search for ─────── */

export type NeedStatus = 'done' | 'todo' | 'optional';

export interface NeedItem {
  id: 'title' | 'location' | 'seniority' | 'companies';
  label: string;
  status: NeedStatus;
  /** guidance shown when the item still needs attention */
  hint?: string;
}

/** Remote/Hybrid/Onsite — the generated wire enum, reused for the panel's mandatory Work-arrangement field. */
export type JobLocationType = components['schemas']['JobLocationType'];

/** Employment-contract type — the generated wire enum, reused for the panel's mandatory Employment-type field. */
export type JobContractType = components['schemas']['JobContractType'];

export const locationTypeOptions: { value: JobLocationType; label: string }[] =
  [
    { value: 'Onsite', label: 'Onsite' },
    { value: 'Hybrid', label: 'Hybrid' },
    { value: 'Remote', label: 'Remote' },
  ];

export const contractTypeOptions: { value: JobContractType; label: string }[] =
  [
    { value: 'EmploymentContract', label: 'Employment Contract' },
    { value: 'B2B', label: 'B2B' },
  ];

/** Narrows a MultiSelect's generic string value to the wire enum (same pattern as {@link isManagementLevel}). */
export const isJobLocationType = (value: string): value is JobLocationType =>
  locationTypeOptions.some((option) => option.value === value);

export const isJobContractType = (value: string): value is JobContractType =>
  contractTypeOptions.some((option) => option.value === value);

/** How much the search leans on a skill, when it isn't normal (finlay-api SkillEmphasisLevel). */
export type EmphasisLevel = 'Low' | 'High';

/** The size of company a job's search prefers candidates to work at now — the generated wire enum. */
export type EmployerSizeBand = components['schemas']['EmployerSizeBand'];

export const employerSizeOptions: { value: EmployerSizeBand; label: string }[] =
  [
    { value: 'Any', label: 'Any size' },
    { value: 'Small', label: 'Up to 200 people' },
    { value: 'Medium', label: '201 to 1,000 people' },
    { value: 'Large', label: 'Over 1,000 people' },
  ];

export const isEmployerSizeBand = (value: string): value is EmployerSizeBand =>
  employerSizeOptions.some((option) => option.value === value);

export interface RoleDraft {
  title: string;
  description: string;
  mustHave: string[];
  niceToHave: string[];
  seniority: string[];
  location: string;
  /** Optional city of an office for onsite or hybrid work. */
  targetCity: string;
  /** Remote/Hybrid/Onsite — mandatory at publish (plan 13); also raw material for later screening questions */
  locationTypes: JobLocationType[];
  /** Where the search looks, as two-letter country codes; a region is several countries. Mandatory at publish,
   * unless the job is open to anywhere. */
  countries: string[];
  /** A remote-only job open to people anywhere: its search has no country filter. */
  isAnywhere: boolean;
  /** How much the search leans on skills in either list, keyed by the skill as the list writes it; others are normal. */
  skillEmphasis: Record<string, EmphasisLevel>;
  /** The size of company the search prefers candidates to work at now. A new job has none until someone picks one,
   * and follows its company's suggestion till then; a saved job always has its own. */
  employerSize?: EmployerSizeBand;
  /** Coresignal's names for the industries the search prefers candidates' current employers to be in; empty is any
   * industry. Like the band, a new job has none until someone picks them, and follows its company's suggestion. */
  employerIndustries?: string[];
  /** Titles the search looks for besides the job's own, such as "Lifecycle Marketer" on a Growth Marketer job. */
  extraSearchTitles: string[];
  /** employment-contract type — mandatory at publish (plan 13) */
  contractTypes: JobContractType[];
  /** optional free-text list of companies known for this role — a recall hint */
  companies: string;
  /** soft/deal-breaker criteria captured as interview questions */
  screeningQuestions: string[];
  exemplarUrl: string;
}

export const emptyRole: RoleDraft = {
  title: '',
  description: '',
  mustHave: [],
  niceToHave: [],
  seniority: [],
  location: '',
  targetCity: '',
  locationTypes: [],
  countries: [],
  isAnywhere: false,
  skillEmphasis: {},
  extraSearchTitles: [],
  contractTypes: [],
  companies: '',
  screeningQuestions: [],
  exemplarUrl: '',
};

/* ── workflow settings (recruiter-only card; derived defaults for customers) ── */

/** Sourcing engine variant — surfaced in the card only as the legacy→Agent migration control (plan 13h). */
type SourcingStrategy = components['schemas']['SourcingStrategy'];

/**
 * The operational settings an internal recruiter can tune (plan 13; also the edit-mode seed via
 * `jobToWorkflowDraft` — plan 13b/13g). Customers never see these — on create their values are derived at publish
 * (owner = current user, company = their tenant, automation = backend defaults); on edit they're echoed from the
 * loaded job. Toggles left `undefined` are omitted from the CREATE request so the backend's own defaults apply;
 * on edit every toggle arrives seeded, so it's always sent explicitly.
 *
 * The lifecycle fields (`state`, `initialReachOutMessageTemplate`, `isMessagingCreative`, `sourcingStrategy`) are
 * EDIT-only (plan 13h): the create wire has no such fields — the platform's model has always been
 * create-then-tune-lifecycle-in-edit (the legacy form silently discarded them at create too). On edit they're
 * seeded from the job, so for customers (no card) they remain faithful echoes automatically.
 */
export interface WorkflowDraft {
  slackChannelId?: string | null;
  slackChannelName?: string | null;
  companyId: string;
  ownerId: string;
  userIds: string[];
  isMessagingAutomated?: boolean;
  isReachOutAutomated?: boolean;
  isShortlistingAutomated?: boolean;
  isInvitationFollowUpEnabled?: boolean;
  isPrescreeningEnabled?: boolean;
  isPublicCandidateListEnabled?: boolean;
  isTrackingCoordinationEvents?: boolean;
  isCoordinationActionAutoApproved?: boolean;
  /** required (pair) only while {@link WorkflowDraft.isTrackingCoordinationEvents} is on */
  coordinationRecruiterId: string;
  coordinationHiringManagerId: string;
  /** edit-only (13h): seeded from the job; undefined on create (jobs are born Unpublished) */
  state?: JobState;
  /** edit-only (13h): seeded from the job; the create API has no template field */
  initialReachOutMessageTemplate: string;
  /** edit-only (13h): seeded from the job; create hardcodes true server-side */
  isMessagingCreative?: boolean;
  /** edit-only (13h): seeded from the job; the card offers only the legacy→Agent migration */
  sourcingStrategy?: SourcingStrategy;
}

export const emptyWorkflow: WorkflowDraft = {
  slackChannelId: null,
  companyId: '',
  ownerId: '',
  userIds: [],
  coordinationRecruiterId: '',
  coordinationHiringManagerId: '',
  initialReachOutMessageTemplate: '',
};

/* ── brief completeness ("health bar") + skill sufficiency ────────────────── */
/* These come from `/ai/job-signal` — a deterministic backend heuristic (PLACEHOLDER; see the plan §9.4). The wire
 * delivers `label`/`nextStep`/`level` as bare `string`; `./verdict` narrows them to the unions below. */

/** Overall brief-completeness bucket (renamed off "readiness"). */
export type CompletenessLabel = 'strong' | 'getting_there' | 'thin';

/** The single highest-impact next action, as a semantic key the UI maps to copy. */
export type NextStep =
  | 'add_title'
  | 'sharpen_title'
  | 'add_searchable_skills'
  | 'add_more_skills'
  | 'add_city'
  | 'set_seniority';

export interface Completeness {
  /** 0..1, drives the health bar */
  strength: number;
  label: CompletenessLabel;
  /** the single highest-impact thing to improve next, or null when nothing obvious is missing */
  nextStep: NextStep | null;
}

/** How well the must-have skills narrow the search. */
export type SufficiencyLevel = 'none' | 'thin' | 'ok';

/**
 * The narrowed `/ai/job-signal` result. `placeholder` is always true today — the scores are the un-validated
 * heuristic, surfaced in the UI as such.
 */
export interface JobSignal {
  completeness: Completeness;
  skillSufficiency: { level: SufficiencyLevel };
  placeholder: boolean;
}

/** The seniority vocabulary — the generated `management_level` wire enum served by finlay-api (plan 9.9), consumed
 * here as the single source of truth so the frontend can't offer a level the backend would reject. */
export type ManagementLevel = components['schemas']['ManagementLevel'];

/**
 * Seniority levels → friendly display labels. The keys ARE the generated {@link ManagementLevel} enum, so this
 * `Record` is exhaustive by construction: adding/removing a backend level becomes a compile error here rather than
 * silent drift (this retires the old hand-maintained value list — plan 9.10). Labels are UI copy and stay
 * frontend-side; only two differ from the raw value.
 */
const SENIORITY_LABELS: Record<ManagementLevel, string> = {
  Intern: 'Intern',
  Specialist: 'Specialist / IC',
  Senior: 'Senior',
  Manager: 'Manager',
  Head: 'Head',
  Director: 'Director',
  'President/Vice President': 'VP / President',
  'C-Level': 'C-Level',
  Owner: 'Owner',
  Founder: 'Founder',
  Partner: 'Partner',
};

/** Dropdown options derived from {@link SENIORITY_LABELS} — no longer a hand-maintained value list. */
export const seniorityOptions: { value: string; label: string }[] =
  Object.entries(SENIORITY_LABELS).map(([value, label]) => ({ value, label }));

/**
 * Narrows a draft's generically-typed seniority string to the wire enum. A stale/renamed level (e.g. from a future
 * restored draft) is dropped by callers rather than sent and rejected with a 400.
 */
export const isManagementLevel = (value: string): value is ManagementLevel =>
  Object.hasOwn(SENIORITY_LABELS, value);

/**
 * A job's inbound setting and Public Job Description as the self-service editor edits them. Sent with create and
 * update only when the editor shows them; omitted, the API keeps the company default (create) or the saved values.
 */
export interface InboundDraft {
  isInboundEnabled: boolean;
  publicJobDescription: string;
}
